import "./styles/app.css";

import type { PlannerController } from "./planner/types";
import { closeSiblingMenus, requiredElement, wireModalDialog } from "./shared/dom";
import { createPlannerIntegration } from "./shared/plannerIntegration";
import {
  defaultCameraPreset,
  loadReviewScene,
  type ReviewScene,
} from "./shared/reviewScene";
import { createStore } from "./shared/store";
import { ProjectGuide } from "./review/projectGuide";
import { ReviewTour } from "./review/tour";
import { ReviewViewer } from "./review/reviewViewer";

type AppMode = "review" | "planner";
type LoadStatus = "loading" | "ready" | "error";

interface AppState {
  mode: AppMode;
  view: string;
  walking: boolean;
  status: LoadStatus;
  furnishingsVisible: boolean;
}

const state = createStore<AppState>({
  mode: "review",
  view: "cutaway",
  walking: false,
  status: "loading",
  furnishingsVisible: false,
});

const manifestUrl = new URL("../review_scene.json", window.location.href);
const canvas = requiredElement<HTMLCanvasElement>("#sceneCanvas");
const viewport = requiredElement<HTMLElement>("#viewport");
const modelStatus = requiredElement<HTMLElement>("#modelStatus");
const loadingCard = requiredElement<HTMLElement>("#loadingCard");
const sceneError = requiredElement<HTMLElement>("#sceneError");
const sceneErrorDetail = requiredElement<HTMLElement>("#sceneErrorDetail");
const revisionLabel = requiredElement<HTMLElement>("#revisionLabel");
const reviewModeButton = requiredElement<HTMLButtonElement>("#reviewModeButton");
const plannerModeButton = requiredElement<HTMLButtonElement>("#plannerModeButton");
const plannerMount = requiredElement<HTMLElement>("#plannerMount");
const experimentalBanner = requiredElement<HTMLElement>("#experimentalBanner");
const walkButton = requiredElement<HTMLButtonElement>("#walkButton");
const walkCrosshair = requiredElement<HTMLElement>("#walkCrosshair");
const interactionHint = requiredElement<HTMLElement>("#interactionHint");
const tourLaunchButton = requiredElement<HTMLButtonElement>("#tourLaunchButton");
const shellToggle = requiredElement<HTMLInputElement>("#shellToggle");
const coreToggle = requiredElement<HTMLInputElement>("#coreToggle");
const liningToggle = requiredElement<HTMLInputElement>("#liningToggle");
const insulationToggle = requiredElement<HTMLInputElement>("#insulationToggle");
const furnishingsToggle = requiredElement<HTMLInputElement>("#furnishingsToggle");

const guideDialog = requiredElement<HTMLDialogElement>("#guideDialog");
const helpDialog = requiredElement<HTMLDialogElement>("#helpDialog");
wireModalDialog(requiredElement("#guideButton"), guideDialog);
wireModalDialog(requiredElement("#helpButton"), helpDialog);
const projectGuide = new ProjectGuide(guideDialog);

let viewer: ReviewViewer | undefined;
let reviewScene: ReviewScene | undefined;
let planner: PlannerController | undefined;
let plannerPromise: Promise<PlannerController> | undefined;
let toastTimer: number | undefined;

const tour = new ReviewTour(requiredElement("#tourCard"), {
  onStep: (hotspot) => viewer?.focusHotspot(hotspot),
  onClose: () => viewer?.clearHotspot(),
});

state.subscribe((next) => renderState(next));
renderState(state.get());
wireControls();
void start();

async function start(): Promise<void> {
  state.set({ status: "loading" });
  loadingCard.hidden = false;
  sceneError.hidden = true;
  let nextViewer: ReviewViewer | undefined;
  try {
    const manifest = await loadReviewScene(manifestUrl);
    nextViewer = new ReviewViewer(canvas, viewport);
    await nextViewer.load(manifest, manifestUrl);
    viewer = nextViewer;
    reviewScene = manifest;
    tour.setHotspots(manifest.review_hotspots);
    tourLaunchButton.disabled = manifest.review_hotspots.length === 0;
    projectGuide.load(new URL("core_interior_builder_brief.json", manifestUrl));

    const requestedView = new URLSearchParams(window.location.search).get("view");
    const initialView = requestedView && manifest.camera_presets.some((preset) => preset.id === requestedView)
      ? requestedView
      : defaultCameraPreset(manifest).id;
    nextViewer.setView(initialView);
    state.set({ status: "ready", view: initialView });
    loadingCard.hidden = true;

    const counts = nextViewer.registry.partCounts;
    modelStatus.lastChild!.textContent = `${counts.shell} shell + ${counts.core_interior} core parts · ready`;
    revisionLabel.textContent = `Core revision ${manifest.revision} · Survey ${manifest.survey_revision}`;
  } catch (error) {
    console.error(error);
    nextViewer?.dispose();
    state.set({ status: "error" });
    loadingCard.hidden = true;
    sceneError.hidden = false;
    sceneErrorDetail.textContent = humanLoadError(error);
    modelStatus.lastChild!.textContent = "Review unavailable";
  }
}

function wireControls(): void {
  for (const details of document.querySelectorAll<HTMLDetailsElement>(".toolbar-menu")) {
    details.addEventListener("toggle", () => {
      if (details.open) closeSiblingMenus(details);
    });
  }
  document.addEventListener("pointerdown", (event) => {
    if (!(event.target instanceof Node)) return;
    for (const details of document.querySelectorAll<HTMLDetailsElement>(".toolbar-menu[open]")) {
      if (!details.contains(event.target)) details.open = false;
    }
  });

  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-view]")) {
    button.addEventListener("click", () => {
      if (!viewer || state.get().status !== "ready") return;
      const id = button.dataset.view!;
      viewer.setView(id);
      state.set({ view: id, walking: false });
      requiredElement<HTMLDetailsElement>("#viewsMenu").open = false;
      viewport.focus({ preventScroll: true });
    });
  }

  reviewModeButton.addEventListener("click", () => void setMode("review"));
  plannerModeButton.addEventListener("click", () => void setMode("planner"));
  walkButton.addEventListener("click", () => {
    if (!viewer) return;
    const active = !viewer.walking;
    viewer.setWalking(active);
    state.set({ walking: active, view: active ? "inside" : viewer.activeView });
    viewport.focus({ preventScroll: true });
  });
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && state.get().walking) {
      viewer?.setWalking(false);
      state.set({ walking: false });
    }
  });

  shellToggle.addEventListener("change", applyLayerVisibility);
  coreToggle.addEventListener("change", applyLayerVisibility);
  liningToggle.addEventListener("change", applyLayerVisibility);
  insulationToggle.addEventListener("change", applyLayerVisibility);
  furnishingsToggle.addEventListener("change", () => void setFurnishingsVisible(furnishingsToggle.checked));

  tourLaunchButton.addEventListener("click", () => tour.start());
  requiredElement<HTMLButtonElement>("#retryButton").addEventListener("click", () => window.location.reload());
  window.addEventListener("beforeunload", () => {
    planner?.destroy();
    viewer?.dispose();
  });
}

function applyLayerVisibility(): void {
  viewer?.setVisibility({
    shell: shellToggle.checked,
    coreInterior: coreToggle.checked,
    linings: liningToggle.checked,
    insulation: insulationToggle.checked,
  });
}

async function setMode(mode: AppMode): Promise<void> {
  if (mode === "planner") {
    try {
      tour.close();
      const controller = await ensurePlanner();
      viewer?.setView("planner");
      plannerMount.hidden = false;
      controller.setActive(true);
      controller.setVisible(true);
      furnishingsToggle.checked = true;
      state.set({ mode, view: "planner", furnishingsVisible: true, walking: false });
      viewer?.setWalking(false);
    } catch (error) {
      console.error(error);
      showToast("The furnishings planner could not be opened. The core review is unchanged.", "error");
    }
    return;
  }

  if (state.get().mode === "planner") viewer?.setView("cutaway");
  planner?.setActive(false);
  planner?.setVisible(false);
  viewer?.setViewportLeftInset(0);
  furnishingsToggle.checked = false;
  plannerMount.hidden = true;
  state.set({ mode, view: "cutaway", furnishingsVisible: false });
}

async function setFurnishingsVisible(visible: boolean): Promise<void> {
  if (!visible) {
    planner?.setVisible(false);
    state.set({ furnishingsVisible: false });
    return;
  }
  try {
    const controller = await ensurePlanner();
    controller.setVisible(true);
    state.set({ furnishingsVisible: true });
  } catch (error) {
    console.error(error);
    furnishingsToggle.checked = false;
    state.set({ furnishingsVisible: false });
    showToast("The saved furnishing idea could not be shown.", "error");
  }
}

function ensurePlanner(): Promise<PlannerController> {
  if (planner) return Promise.resolve(planner);
  if (!plannerPromise) {
    if (!viewer || !reviewScene) return Promise.reject(new Error("The issued review must load before the planner"));
    plannerPromise = createPlannerIntegration({
      mount: plannerMount,
      viewer,
      reviewScene,
      onVisibilityChange: (visible) => {
        furnishingsToggle.checked = visible;
        state.set({ furnishingsVisible: visible });
      },
      onToast: showToast,
    }).then((controller) => {
      planner = controller;
      return controller;
    });
  }
  return plannerPromise;
}

function renderState(next: Readonly<AppState>): void {
  reviewModeButton.setAttribute("aria-pressed", String(next.mode === "review"));
  plannerModeButton.setAttribute("aria-pressed", String(next.mode === "planner"));
  reviewModeButton.classList.toggle("is-active", next.mode === "review");
  plannerModeButton.classList.toggle("is-active", next.mode === "planner");
  experimentalBanner.hidden = next.mode !== "planner" && !next.furnishingsVisible;

  walkButton.setAttribute("aria-pressed", String(next.walking));
  walkButton.textContent = next.walking ? "Exit walk" : "Walk inside";
  walkCrosshair.hidden = !next.walking;
  interactionHint.textContent = next.walking
    ? "Drag to look around · Escape to exit"
    : "Drag to orbit · pinch or wheel to zoom";
  interactionHint.hidden = next.mode === "planner";
  tourLaunchButton.hidden = next.mode === "planner";

  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-view]")) {
    button.setAttribute("aria-pressed", String(button.dataset.view === next.view));
  }

  modelStatus.classList.toggle("is-ready", next.status === "ready");
  modelStatus.classList.toggle("is-error", next.status === "error");
  for (const control of document.querySelectorAll<HTMLButtonElement | HTMLInputElement>(
    "[data-view], #walkButton, #tourLaunchButton, #shellToggle, #coreToggle, #liningToggle, #insulationToggle, #furnishingsToggle",
  )) control.disabled = next.status !== "ready";
  walkButton.disabled = next.status !== "ready" || next.mode === "planner";
}

function showToast(message: string, tone: "info" | "warning" | "error" = "info"): void {
  let toast = document.querySelector<HTMLElement>(".app-toast");
  if (!toast) {
    toast = document.createElement("div");
    toast.className = "app-toast";
    toast.setAttribute("role", "status");
    document.body.append(toast);
  }
  toast.dataset.tone = tone;
  toast.textContent = message;
  toast.hidden = false;
  if (toastTimer !== undefined) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    if (toast) toast.hidden = true;
  }, 5_000);
}

function humanLoadError(error: unknown): string {
  if (!(error instanceof Error)) return "Regenerate the package, then reload this page.";
  if (error.name === "ReviewSceneValidationError") return "The generated review data is incompatible with this viewer. Regenerate the package.";
  if (error.name === "AssetIntegrityError") return "A model file does not match the issued review data. Regenerate the package.";
  return `${error.message}. Regenerate the package, then reload.`;
}
